import { emptyProfileRegistrationForm } from "@/lib/profileRegistrationForm";
import { parseOptionalAge } from "@/lib/mapRegisteredParticipantToAzdanceApi";
import { normalizeChampionshipCategoryForUi } from "@/lib/participantFieldOptions";
import type { ParticipantFormData } from "./registrationFlowTypes";
import {
    mapRegisteredParticipantsToParticipantFormData,
    mergeCopartnerRegisteredParticipants,
} from "./registrationFlowMappers";
import type { RegisteredParticipant } from "@/state/stores/registrationStore";

/** Пустая строка воронки: базовые поля из {@link emptyProfileRegistrationForm}. */
export function emptyParticipantFormData(): ParticipantFormData {
    return {
        ...emptyProfileRegistrationForm(),
        instagram: "",
        isChampionshipParticipant: false,
        isSolo: false,
        lookingForPartner: null,
        partner2FirstName: "",
        partner2LastName: "",
        partner2Age: "",
        partner2Email: "",
        partner2Phone: "",
        partner2Country: "",
        partner2City: "",
        partner2Gender: "",
        partner2Level: "",
        partner2Role: "",
        partner2ContactType: "",
        partner2ContactValue: "",
        partner2Instagram: "",
        category: "",
        isExpanded: true,
    };
}

/**
 * Строка формы воронки → патч одной записи {@link RegisteredParticipant} в сторе
 * (карточка Partner 2 как отдельного участника).
 */
export function participantFormDataToStoreParticipantPatch(
    row: ParticipantFormData,
    opts: {
        instagram?: string;
        partnerId?: string;
    } = {},
): Partial<RegisteredParticipant> {
    return {
        firstName: row.firstName.trim(),
        lastName: row.lastName.trim(),
        age: parseOptionalAge(row.age),
        email: row.email.trim(),
        phone: row.phone || "",
        country: row.country,
        city: row.city.trim(),
        location: "",
        gender: row.gender,
        role: row.role,
        level: row.level,
        contactType: row.contactType,
        contactValue: row.contactValue,
        instagram: opts.instagram,
        isChampionshipParticipant: row.isChampionshipParticipant,
        registrationMode: row.isChampionshipParticipant
            ? "championship"
            : "default",
        isSolo: row.isSolo,
        lookingForPartner: row.lookingForPartner,
        category: normalizeChampionshipCategoryForUi(row.category),
        ...(opts.partnerId != null ? { partnerId: opts.partnerId } : {}),
    };
}

/** Встроенные поля partner2 в одной строке формы — сброс (solo / выход с чемпионата). */
export const CLEAR_EMBEDDED_PARTNER2_ON_FORM_ROW: Partial<ParticipantFormData> = {
    partner2FirstName: "",
    partner2LastName: "",
    partner2Age: "",
    partner2Email: "",
    partner2Phone: "",
    partner2Country: "",
    partner2City: "",
    partner2Gender: "",
    partner2Level: "",
    partner2Role: "",
    partner2ContactType: "",
    partner2ContactValue: "",
    partner2Instagram: "",
};

/** С чемпионата → фестиваль: очистить второго партнёра и категорию в строке формы. */
export const CLEAR_CHAMPIONSHIP_PARTNER_ON_FORM_ROW: Partial<ParticipantFormData> =
    {
        isSolo: false,
        lookingForPartner: null,
        partnerId: undefined,
        category: "",
        ...CLEAR_EMBEDDED_PARTNER2_ON_FORM_ROW,
    };

/**
 * Обновление одной строки формы воронки (gender mirror, сброс partner2 при выходе с чемпионата).
 * Сторонние эффекты (registerParticipants после смены режима) — у вызывающего.
 */
export function mergeParticipantFormRowUpdate(
    prev: ParticipantFormData,
    patch: Partial<ParticipantFormData>,
): ParticipantFormData {
    const p: ParticipantFormData = { ...prev, ...patch };

    if (p.isChampionshipParticipant) {
        if (patch.gender === "mr" || patch.gender === "ms") {
            p.partner2Gender = patch.gender === "mr" ? "ms" : "mr";
        } else if (
            patch.partner2Gender === "mr" ||
            patch.partner2Gender === "ms"
        ) {
            p.gender = patch.partner2Gender === "mr" ? "ms" : "mr";
        } else if (patch.isChampionshipParticipant === true) {
            const gOk = p.gender === "mr" || p.gender === "ms";
            const p2Ok =
                p.partner2Gender === "mr" || p.partner2Gender === "ms";
            if (gOk && !p2Ok) {
                p.partner2Gender = p.gender === "mr" ? "ms" : "mr";
            } else if (p2Ok && !gOk) {
                p.gender = p.partner2Gender === "mr" ? "ms" : "mr";
            }
        }
        if (
            (p.gender === "mr" || p.gender === "ms") &&
            (p.partner2Gender === "mr" || p.partner2Gender === "ms") &&
            p.gender === p.partner2Gender
        ) {
            p.partner2Gender = p.gender === "mr" ? "ms" : "mr";
        }
    }

    if (prev.isChampionshipParticipant && !p.isChampionshipParticipant) {
        Object.assign(p, CLEAR_CHAMPIONSHIP_PARTNER_ON_FORM_ROW);
    }

    /** Solo: не отправляем partner2 на бэк и не валидируем — поля обнуляются. */
    if (p.isChampionshipParticipant && p.isSolo) {
        Object.assign(p, CLEAR_EMBEDDED_PARTNER2_ON_FORM_ROW);
    }

    /** «Ищу партнёра» — только для фестиваля (уроки), не для чемпионата. */
    if (p.isChampionshipParticipant) {
        p.lookingForPartner = null;
    }

    return p;
}

/**
 * Строка {@link ParticipantFormData} для модалки редактирования: та же схлопнутая пара, что и на registration-flow.
 * Для `isPartner2` — одна строка только этого участника (без полей partner2*).
 */
export function resolveParticipantFormRowForModalEdit(
    participant: RegisteredParticipant,
    allRegistered: RegisteredParticipant[],
): ParticipantFormData {
    if (participant.isPartner2) {
        const [row] = mapRegisteredParticipantsToParticipantFormData([
            participant,
        ]);
        return { ...row, isExpanded: true };
    }
    const merged = mergeCopartnerRegisteredParticipants(allRegistered);
    const forms = mapRegisteredParticipantsToParticipantFormData(merged);
    const pid = participant.id?.trim() ?? "";

    let byId = forms.find((f) => f.id === pid);
    /** Карточка соучастника в сайдбаре: id = partner2, в форме строка с `id` primary и `partnerId` = partner2. */
    if (!byId) {
        byId = forms.find((f) => (f.partnerId ?? "").trim() === pid);
    }
    if (!byId) {
        const link = participant.partnerId?.trim();
        if (link) {
            byId = forms.find((f) => f.id === link);
        }
    }
    if (byId) return { ...byId, isExpanded: true };

    const [solo] = mapRegisteredParticipantsToParticipantFormData([
        participant,
    ]);
    return { ...solo, isExpanded: true };
}
